import { DocumentNode, gql, useQuery } from '@apollo/client';
import { useWeb3React } from '@web3-react/core';
import { useEffect, useState } from 'react';
import { SwapDto, SwapsDto } from '../../types/dto/Swap.dto';
import { useKiwiClient } from './useClient';
import { SwapStatus } from '../../types/enums';

interface SwapsHistory {
  loading: boolean;
  error?: string;
  data?: SwapDto[];
}

export const useSwapHistory = (last: number, statuses: SwapStatus[]): SwapsHistory => {
  const [sortedSwaps, setSortedSwaps] = useState<SwapDto[]>([]);
  const { account } = useWeb3React();
  const apolloClient = useKiwiClient();

  if (!account) {
    return {
      loading: false,
    };
  }

  const baseVars = {
    orderBy: 'createdAt',
    where: { status: { operator: 'in', value: statuses } },
  };

  const fromVars = {
    ...baseVars,
    where: {
      ...baseVars.where,
      from: { value: account.toLowerCase() },
    },
  };

  const toVars = {
    ...baseVars,
    where: {
      ...baseVars.where,
      to: { value: account.toLowerCase() },
    },
  };

  const { loading: loadingFrom, error: errorFrom, data: dataFrom } = useQuery<SwapsDto>(FETCH_SWAP_HISTORY(), {
    client: apolloClient,
    variables: {
      filters: JSON.stringify(fromVars),
    },
  });

  const { loading: loadingTo, error: errorTo, data: dataTo } = useQuery<SwapsDto>(FETCH_SWAP_HISTORY(), {
    client: apolloClient,
    variables: {
      filters: JSON.stringify(toVars),
    },
  });

  // Make operations on swap array only when one of the queries gets new results
  useEffect(() => {
    if (!loadingTo && !loadingFrom) {
      // Both queries are finished with loading
      const allSwaps = [...(dataTo?.swaps || []), ...(dataFrom?.swaps || [])];
      const sorted = allSwaps.sort((firstSwap, secondSwap): number => {
        return firstSwap.createdAt < secondSwap.createdAt ? 1 : -1;
      });
      setSortedSwaps(sorted);
    }
  }, [dataTo?.swaps, dataFrom?.swaps]);

  return {
    loading: loadingFrom || loadingTo,
    error: errorFrom?.message || errorTo?.message,
    data: sortedSwaps,
  };
};

const FETCH_SWAP_HISTORY = (): DocumentNode =>
  gql(
    `query Swap($filters: String!) {
     swaps(filters: $filters) {
      id
      contract
      status
      createdAt
      updatedAt
      canceledBy {
        id
      }
      rejectedBy {
        id
      }
      initiatorNfts {
        id
        token {
          id
        }
        tokenUri
        identifier
        amount
      }
      initiatorEther
      secondUserNfts {
        id
        token {
          id
        }
        tokenUri
        identifier
        amount
      }
      secondUserEther
      from {
        id
      }
      to {
        id
      }
    }
  }
  `,
  );
